/**
 * Rubicon service categorization and modernization index.
 *
 * Pure functions — no React, no Cloudscape — so they are testable in isolation,
 * following the pattern of components/pricingCalculatorUtils.js.
 *
 * Rubicon restricts no AWS service and excludes no consumption, so this does NOT
 * reuse the backend's `modernizationPathways`: that payload bases ARR on
 * `map_qualified_mrr` (i.e. POST-MAP-exclusion) and classifies from the
 * chronos_modpathway table with a MAP-only "30% of EC2 counts as Containers"
 * rule. Rubicon's base is the full calculator cost.
 *
 * Category tiers come from `AWS Services Categories 2026.pdf` v2.0; the
 * service -> category mapping is data, in ../data/rubiconServiceCategories.json.
 * Cat 2 and Cat 3 are "modern"; Cat 1 is not; Unmapped is not.
 */
import CATEGORY_ROWS from '../data/rubiconServiceCategories.json';
import SUBSERVICE_PARENTS from '../data/rubiconSubserviceParents.json';

/** Sentinel for a service code with no category. Deliberately not a number. */
export const UNMAPPED = 'unmapped';

const BY_CODE = new Map(CATEGORY_ROWS.map((row) => [row.service_code, row]));

// Guards against a malformed parent chain in the data files sending
// resolveCategory into infinite recursion.
const MAX_PARENT_DEPTH = 8;

/**
 * AWS Support detection, mirroring the backend's ALWAYS_EXCLUDED_PATTERNS
 * substring test in funding_calculator/eligible_services.py.
 *
 * Only the "support" pattern is mirrored — NOT the rest of the backend's
 * always-excluded list (Glacier Deep Archive, S3 data transfer). Those are MAP
 * exclusions, and Rubicon deliberately does not apply them.
 */
export const isAwsSupport = (serviceCode) =>
  String(serviceCode || '').toLowerCase().includes('support');

/**
 * Resolve a calculator service code to its category table row, or null.
 * Follows the sub-service -> parent chain first.
 */
export const resolveCategoryRow = (serviceCode, _depth = 0) => {
  if (!serviceCode) return null;

  // 1. Sub-service inherits its parent. MUST run before the regex fallbacks
  //    below: `auroraBackup` and `rdsBackup` are children of `awsBackup`
  //    (Storage -> Cat 1), and /aurora/i would otherwise promote AWS Backup
  //    spend into Cat 3.
  const parent = SUBSERVICE_PARENTS[serviceCode];
  if (parent && _depth < MAX_PARENT_DEPTH) {
    return resolveCategoryRow(parent, _depth + 1);
  }

  // 2. Exact code match against the curated table.
  return BY_CODE.get(serviceCode) || null;
};

/**
 * Resolve a calculator service code to 1, 2, 3, or UNMAPPED.
 *
 * Order matters twice over:
 *   - sub-service resolution precedes the regex fallbacks (see above);
 *   - the Aurora test precedes the RDS test, because
 *     `amazonRDSAuroraPostgreSQLCompatibleDB` satisfies BOTH and all RDS Aurora
 *     is Category 3 while plain RDS is Category 2. Reversing them silently
 *     files Aurora as Cat 2.
 *
 * The two regexes exist only so a FUTURE Aurora/RDS code (Aurora DSQL, Aurora
 * Serverless, Aurora Limitless) lands correctly without a data edit. Every code
 * known today is pinned exactly in the JSON.
 */
export const resolveCategory = (serviceCode) => {
  const row = resolveCategoryRow(serviceCode);
  if (row) return row.rubicon_category;

  if (/aurora/i.test(serviceCode)) return 3;
  if (/^amazonRds/i.test(serviceCode)) return 2;

  return UNMAPPED;
};

/**
 * Resolve a whole service object, falling back to its parent service code.
 *
 * Necessary because process_calculator flattens some nested estimates so that
 * `service_code` holds a per-line VENDOR code while the actual AWS service sits
 * in `parent_service_code`. Amazon Bedrock is the live case: rows arrive with
 * service_code='anthropic' (or 'amazon') and parent_service_code='amazonBedrock'.
 * Resolving on `service_code` alone files all of that Bedrock spend as Unmapped.
 *
 * Prefer this over resolveCategory() wherever a full service object is in hand.
 */
export const resolveServiceCategory = (service) => {
  const direct = resolveCategory(service?.service_code);
  if (direct !== UNMAPPED) return direct;
  if (service?.parent_service_code) return resolveCategory(service.parent_service_code);
  return UNMAPPED;
};

/** Cat 2 and Cat 3 are modern. Cat 1 and UNMAPPED are not. */
export const isModern = (category) => category === 2 || category === 3;

export const categoryLabel = (category) =>
  category === UNMAPPED ? 'Unmapped' : `Category ${category}`;

/**
 * Three-year commitment detection, matching PricingCalculatorTab's isThreeYearTerm
 * so Rubicon amortizes upfront cost the same way MAP does.
 */
export const isThreeYearTerm = (term) => {
  if (!term) return false;
  const t = String(term).toLowerCase().trim();
  return (
    ['3yr', '3 yr', '3year', '3 year'].includes(t) ||
    /\b3\s*(yr|year)s?\b/.test(t) ||
    /\b36\s*(month|mo)s?\b/.test(t)
  );
};

/**
 * Annual run rate for one calculator line item.
 *
 * Full calculator cost — no MAP exclusions. A 3-year upfront is amortized to
 * one year's share; anything else counts in full, matching the backend's
 * amortize_upfront().
 */
export const serviceARR = (service) => {
  const monthly = Number(service?.monthly_cost) || 0;
  const upfront = Number(service?.upfront_cost) || 0;
  const amortized = isThreeYearTerm(service?.pricing_strategy_term) ? upfront / 3 : upfront;
  return monthly * 12 + amortized;
};

const emptyBucket = () => ({ arr: 0, pct: 0, serviceCount: 0 });

/**
 * Compute the Rubicon modernization index over a calculator's services.
 *
 *   totalARR  = Cat1 + Cat2 + Cat3 + Unmapped      (denominator)
 *   modernARR = Cat2 + Cat3                        (numerator)
 *   index     = modernARR / totalARR * 100
 *
 * Unmapped ARR is COUNTED IN THE TOTAL but never as modern, so the Rubicon
 * total reconciles against the raw calculator total and a gap in the mapping
 * drags the index down rather than flattering it.
 *
 * AWS Support is removed from everything — total, numerator, denominator — and
 * reported separately as `supportARR` so the difference from the raw calculator
 * total stays auditable instead of silently appearing.
 *
 * @param {Array} services raw `services[]` from the calculator payload
 */
export const computeRubiconIndex = (services) => {
  const byCategory = { 1: emptyBucket(), 2: emptyBucket(), 3: emptyBucket(), [UNMAPPED]: emptyBucket() };
  const unmapped = [];
  let totalARR = 0;
  let modernARR = 0;
  let supportARR = 0;
  let supportServiceCount = 0;

  for (const service of services || []) {
    const code = service?.service_code || '';
    const arr = serviceARR(service);

    if (isAwsSupport(code)) {
      supportARR += arr;
      supportServiceCount += 1;
      continue;
    }

    const category = resolveServiceCategory(service);
    const bucket = byCategory[category];
    bucket.arr += arr;
    bucket.serviceCount += 1;

    totalARR += arr;
    if (isModern(category)) modernARR += arr;

    if (category === UNMAPPED) {
      unmapped.push({ code, name: service?.service_name || code, arr });
    }
  }

  for (const key of [1, 2, 3, UNMAPPED]) {
    byCategory[key].pct = totalARR > 0 ? (byCategory[key].arr / totalARR) * 100 : 0;
  }

  const serviceCount = (services || []).length;

  return {
    index: totalARR > 0 ? (modernARR / totalARR) * 100 : 0,
    totalARR,
    modernARR,
    byCategory,
    unmapped,
    unmappedARR: byCategory[UNMAPPED].arr,
    supportARR,
    supportServiceCount,
    serviceCount,
    /**
     * True when the estimate lists services but carries no cost for ANY of them.
     *
     * Real case: calculator 366bd0e6a67364de9fa0be9632f7fb04d7fc7a01 contains 963
     * services, none of which has a `serviceCost` key — only a single top-level
     * `totalCost` ($1.15M). process_calculator reads
     * `service_data.get('serviceCost', {}).get('monthly', 0)`, so every line
     * silently becomes 0 rather than raising. Without this flag the UI would
     * confidently report $0 ARR and a 0% modernization index for a
     * seven-figure estimate — the worst kind of wrong, because it looks answered.
     */
    hasNoCostData: serviceCount > 0 && totalARR + supportARR === 0,
  };
};

/** Rubicon's modernization target. Projects at or above this are on track. */
export const MODERNIZATION_TARGET_PCT = 40;
